/**
 * Chat utility functions
 * Extracted from Chat.page.tsx for better organization and reusability
 */

import type { ModelInfo, RateLimitStatus } from '@gofranz/gpt-muse-common';

/**
 * Formats a relative time until a timestamp resets
 * @param resetTimestamp Unix timestamp in seconds
 * @returns Human-readable time string
 */
export function formatTimeUntilReset(resetTimestamp: number): string {
  const now = Date.now();
  const resetTime = resetTimestamp * 1000; // Convert to milliseconds
  const timeDiff = resetTime - now;

  if (timeDiff <= 0) {
    return 'now';
  }

  const hours = Math.floor(timeDiff / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}${minutes > 0 ? ` ${minutes} minute${minutes !== 1 ? 's' : ''}` : ''}`;
  } else {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
}

/**
 * Formats a date relative to now
 * @param date Date to format
 * @returns Human-readable relative date string
 */
export function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Generates a conversation title from the first user message
 * @param content Message content
 * @param maxLength Maximum title length (default: 50)
 * @returns Truncated title
 */
export function generateConversationTitle(content: string, maxLength: number = 50): string {
  return content.slice(0, maxLength) + (content.length > maxLength ? '...' : '');
}

/**
 * Validates if a model ID exists in a list of models
 * @param modelId Model ID to validate
 * @param models Array of available models
 * @returns Boolean indicating if model exists
 */
export function isValidModel(modelId: string, models: ModelInfo[]): boolean {
  return models.some((model) => model.id === modelId);
}

/**
 * Gets the default model from a list of models
 * @param models Array of available models
 * @returns Default model ID or empty string
 */
export function getDefaultModel(models: ModelInfo[]): string {
  if (models.length === 0) return '';

  // Prefer gemini-2.5-flash-lite if available
  const preferredModel = models.find((model) => model.id === 'gemini-2.5-flash-lite');
  if (preferredModel) {
    return preferredModel.id;
  }

  // Fall back to first available model
  return models[0].id;
}

/**
 * Formats rate limit tooltip content
 * @param rateLimitInfo Rate limit status object
 * @returns Formatted tooltip string
 */
export function formatRateLimitTooltip(rateLimitInfo: RateLimitStatus): string {
  return `You're not logged in. Rate limits apply:

• Remaining: ${rateLimitInfo.remaining_requests}/${rateLimitInfo.limit_requests} requests
• Resets: ${new Date(rateLimitInfo.reset_time * 1000).toLocaleString('en-US', {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })} (in ${formatTimeUntilReset(rateLimitInfo.reset_time)})

Sign up for unlimited access!`;
}

/**
 * Determines badge color based on rate limit usage
 * @param remaining Remaining requests
 * @param total Total request limit
 * @returns Color string for badge
 */
export function getRateLimitBadgeColor(remaining: number, total: number): string {
  const ratio = remaining / total;

  if (ratio > 0.5) return 'green';
  if (ratio > 0.2) return 'yellow';
  return 'red';
}

/**
 * Domain information extracted from links
 */
export interface DomainInfo {
  domain: string;
  url: string;
}

/**
 * Extracts unique URLs and their domains from HTML content
 * @param htmlContent HTML content to parse
 * @returns Array of unique URLs with their domains
 */
export function extractDomainsFromHtml(htmlContent: string): DomainInfo[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const links = doc.querySelectorAll('a[href]');

  const urlSet = new Set<string>(); // Track unique URLs instead of domains
  const domainInfos: DomainInfo[] = [];

  links.forEach((link) => {
    try {
      const href = link.getAttribute('href');
      if (!href) return;

      const url = new URL(href);
      const domain = url.hostname;

      // Only include domains that look like actual websites (have at least one dot)
      // Check URL uniqueness instead of domain uniqueness to show all links
      if (domain.includes('.') && !urlSet.has(href)) {
        urlSet.add(href);
        domainInfos.push({ domain, url: href });
      }
    } catch (error) {
      // Invalid URL, skip it
    }
  });

  return domainInfos;
}
